import axios, { AxiosHeaders, AxiosResponse } from 'axios';
import * as SecureStore from 'expo-secure-store';
import { Platform } from 'react-native';
import {
  User,
  Document,
  ChatSession,
  ChatMessage,
  ChatRequest,
  ChatResponse,
  AsyncChatResponse,
  AsyncAnalysisResponse,
  TaskStatus,
  AuthResponse,
  TableExportRequest,
  CollectionStats,
  IntakeSummary,
  SchoolPlanPreview,
  CreditStatus,
  CreditPackList,
  TranscriptContent,
  TranscriptSegment,
  TranscriptionUploadResponse,
  TranscriptionListResponse,
  TranscriptionStatusResponse,
  TranscriptionAudioResponse,
  TranscriptionFormatsResponse,
  TranscriptionOptions,
  TranscriptionUsageResponse,
  TranscriptAnalysesResponse,
  TranscriptAnalyzeResponse,
  AnalysisOptions,
  UnifiedAIResponse,
  ToolResult,
} from '../types';
import { createIdempotencyKey, messageIdempotencyHeaders } from './idempotencyIntent';
import { apiErrorFromBody } from '../utils/trialAnalysisUsed';
import { transcribeErrorFromResponse } from '../utils/transcribeError';
import { parseRetryAfterMs } from '../utils/authLoad';

const RAW_API_BASE_URL = process.env.EXPO_PUBLIC_API_URL || 'http://localhost:8000';
function normalizeBase(url: string): string {
  if (!url) return '';
  const hasScheme = /^https?:\/\//i.test(url);
  // In the browser: only upgrade to https when the page itself is https
  if (typeof window !== 'undefined' && window.location?.protocol === 'https:') {
    if (!hasScheme) return `https://${url}`;
    if (url.toLowerCase().startsWith('http://')) return url.replace(/^http:\/\//i, 'https://');
    return url;
  }
  // In dev / non-https page: keep http to avoid TLS errors
  if (!hasScheme) return `http://${url}`;
  return url;
}
export const API_BASE_URL = normalizeBase(RAW_API_BASE_URL);
export const USE_COOKIES = (() => {
  const envVal = process.env.EXPO_PUBLIC_USE_COOKIES;
  if (typeof envVal === 'string') return envVal === 'true';
  if (typeof window !== 'undefined') return window.location?.protocol === 'https:';
  return false;
})();

const TOKEN_KEY = 'auth_token';
const REFRESH_TOKEN_KEY = 'refresh_token';

// Lives in idempotencyIntent.ts (dependency-free, unit-tested); re-exported
// here for existing importers.
export { createIdempotencyKey };

function isStripeMutationRequest(method?: string, url?: string): boolean {
  if ((method || 'get').toLowerCase() !== 'post' || !url) return false;
  const path = url.split('?')[0];
  return (
    /^\/api\/billing\/(checkout|portal|change-plan|cancel|resume)$/.test(path) ||
    /^\/api\/billing\/schools\/[^/]+\/(admin-checkout|plan-selection-checkout|plan-checkout|update-seat-capacity)$/.test(path) ||
    path === '/api/credits/checkout' ||
    path === '/api/transcriptions/minute-packs/checkout'
  );
}

const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: USE_COOKIES,
  headers: {
    'Content-Type': 'application/json',
  },
});

const refreshClient = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: USE_COOKIES,
  headers: {
    'Content-Type': 'application/json',
  },
});

let onUnauthorizedHandler: (() => void) | null = null;
export const setOnUnauthorizedHandler = (handler: (() => void) | null) => {
  onUnauthorizedHandler = handler;
};

export const notifyUnauthorized = () => {
  if (onUnauthorizedHandler) {
    try {
      onUnauthorizedHandler();
    } catch { }
  }
};

const isWeb = Platform.OS === 'web';

export const getAccessToken = async (): Promise<string | null> => {
  if (USE_COOKIES) return null;
  if (isWeb) {
    try {
      return localStorage.getItem(TOKEN_KEY);
    } catch {
      return null;
    }
  }
  try {
    return await SecureStore.getItemAsync(TOKEN_KEY);
  } catch {
    return null;
  }
};

const setAccessToken = async (token: string): Promise<void> => {
  if (USE_COOKIES) return;
  if (isWeb) {
    try {
      localStorage.setItem(TOKEN_KEY, token);
    } catch { }
  } else {
    await SecureStore.setItemAsync(TOKEN_KEY, token);
  }
};

const removeAccessToken = async (): Promise<void> => {
  if (USE_COOKIES) return;
  console.log('🔓 Removing access token...');
  if (isWeb) {
    try {
      localStorage.removeItem(TOKEN_KEY);
    } catch { }
  } else {
    await SecureStore.deleteItemAsync(TOKEN_KEY);
  }
};

export const getRefreshToken = async (): Promise<string | null> => {
  if (USE_COOKIES) return null;
  // On web, never store/return a refresh token from JS-readable storage.
  // The browser flow MUST use HttpOnly cookies — anything else is XSS-stealable.
  if (isWeb) return null;
  try {
    return await SecureStore.getItemAsync(REFRESH_TOKEN_KEY);
  } catch {
    return null;
  }
};

const setRefreshToken = async (token: string): Promise<void> => {
  if (USE_COOKIES) return;
  // Drop on the floor for web — the backend should not be sending refresh
  // tokens to browser clients in JSON anyway, but if it slips through we
  // refuse to persist it to localStorage where XSS could read it.
  if (isWeb) return;
  await SecureStore.setItemAsync(REFRESH_TOKEN_KEY, token);
};

const removeRefreshToken = async (): Promise<void> => {
  if (USE_COOKIES) return;
  if (isWeb) {
    // Best-effort cleanup of any legacy localStorage refresh tokens left over
    // from before this hardening was in place.
    try {
      localStorage.removeItem(REFRESH_TOKEN_KEY);
    } catch { }
    return;
  }
  await SecureStore.deleteItemAsync(REFRESH_TOKEN_KEY);
};

export const clearStoredTokens = async (): Promise<void> => {
  await Promise.all([removeAccessToken(), removeRefreshToken()]);
};

const findHeaderValue = (headers: any, name: string): string | undefined => {
  if (!headers) return undefined;
  const target = name.toLowerCase();
  for (const key of Object.keys(headers)) {
    if (key.toLowerCase() === target) {
      const value = (headers as any)[key];
      if (typeof value === 'string') return value;
      if (Array.isArray(value) && value.length > 0) return value[0];
    }
  }
  return undefined;
};

const persistTokens = async (accessToken?: string | null, refreshToken?: string | null) => {
  if (USE_COOKIES) return;
  const trimmedAccess = accessToken?.trim();
  const trimmedRefresh = refreshToken?.trim();
  if (trimmedAccess) {
    await setAccessToken(trimmedAccess);
  }
  if (trimmedRefresh) {
    await setRefreshToken(trimmedRefresh);
  }
};

const handleAuthResponse = async (response: AxiosResponse<AuthResponse>): Promise<AuthResponse> => {
  const accessToken = response.data?.access_token ?? (!USE_COOKIES ? findHeaderValue(response.headers, 'x-new-access-token') : undefined);
  const refreshToken = response.data?.refresh_token ?? (!USE_COOKIES ? findHeaderValue(response.headers, 'x-new-refresh-token') : undefined);
  await persistTokens(accessToken, refreshToken);
  // Reset cached auth state so the next calls fetch fresh user/billing data
  clearApiGetCache();
  currentUserCache = { value: null, ts: 0 };
  currentUserPromise = null;
  billingStatusCache = { value: null, ts: 0 };
  billingStatusPromise = null;
  return response.data;
};

/**
 * Result of a refresh attempt.
 * - 'refreshed': new tokens stored.
 * - 'rejected': the session is gone (401/400, no refresh token); log out.
 * - 'unavailable': the auth backend is temporarily down (502/503/504, e.g. a
 *   Redis blip). The session may still be valid, so keep it and let the next
 *   request try again instead of logging the user out.
 */
export type RefreshOutcome = 'refreshed' | 'rejected' | 'unavailable';

const TRANSIENT_REFRESH_STATUSES = new Set([502, 503, 504]);

let refreshPromise: Promise<RefreshOutcome> | null = null;
const REFRESH_PATH = '/api/auth/refresh';

const performRefreshRequest = async (): Promise<RefreshOutcome> => {
  try {
    if (USE_COOKIES) {
      const response = await refreshClient.post<AuthResponse>(REFRESH_PATH, {});
      return (await handleAuthResponse(response)) ? 'refreshed' : 'rejected';
    }
    const storedRefresh = await getRefreshToken();
    if (!storedRefresh) {
      return 'rejected';
    }
    const response = await refreshClient.post<AuthResponse>(REFRESH_PATH, { refresh_token: storedRefresh });
    return (await handleAuthResponse(response)) ? 'refreshed' : 'rejected';
  } catch (error) {
    const status = (error as { response?: { status?: number } })?.response?.status;
    return status !== undefined && TRANSIENT_REFRESH_STATUSES.has(status) ? 'unavailable' : 'rejected';
  }
};

/** Single-flight refresh that reports why it failed (see RefreshOutcome). */
export const refreshSession = async (): Promise<RefreshOutcome> => {
  if (!refreshPromise) {
    refreshPromise = (async () => {
      try {
        return await performRefreshRequest();
      } finally {
        refreshPromise = null;
      }
    })();
  }
  return refreshPromise;
};

export const attemptRefresh = async (): Promise<boolean> => (await refreshSession()) === 'refreshed';

// Attach interceptors after helpers are defined
api.interceptors.request.use(async (config) => {
  if (isStripeMutationRequest(config.method, config.url)) {
    config.headers = (config.headers || new AxiosHeaders()) as AxiosHeaders;
    const headers = config.headers as Record<string, string>;
    if (!headers['Idempotency-Key'] && !headers['idempotency-key']) {
      headers['Idempotency-Key'] = createIdempotencyKey();
    }
  }
  if (!USE_COOKIES) {
    const token = await getAccessToken();
    if (token) {
      config.headers = (config.headers || new AxiosHeaders()) as AxiosHeaders;
      (config.headers as Record<string, string>).Authorization = `Bearer ${token}`;
    }
  }
  // Dev-only geo override header for currency testing
  try {
    const envDevCountry = process.env.EXPO_PUBLIC_DEV_COUNTRY;
    let devCountry: string | undefined = typeof envDevCountry === 'string' ? envDevCountry : undefined;
    if (!devCountry && isWeb) {
      try {
        const lc = localStorage.getItem('dev_country');
        if (lc) devCountry = lc;
      } catch { }
    }
    if (devCountry) {
      config.headers = (config.headers || new AxiosHeaders()) as AxiosHeaders;
      (config.headers as Record<string, string>)['X-Dev-Country'] = devCountry.toUpperCase().slice(0, 2);
    }
  } catch { }
  return config;
});

api.interceptors.response.use(
  async (response) => {
    if (!USE_COOKIES) {
      const headerAccess = findHeaderValue(response.headers, 'x-new-access-token');
      const headerRefresh = findHeaderValue(response.headers, 'x-new-refresh-token');
      if (headerAccess || headerRefresh) {
        await persistTokens(headerAccess, headerRefresh);
      }
    }
    return response;
  },
  async (error) => {
    const { response: errorResponse } = error;
    const originalRequest = error.config as any;
    if (errorResponse?.status === 401) {
      const suppressUnauthorized = Boolean(originalRequest?._suppressUnauthorized);
      const skipAuthRefresh = Boolean(originalRequest?._skipAuthRefresh);
      const isRefreshCall = typeof originalRequest?.url === 'string' && originalRequest.url.includes(REFRESH_PATH);
      if (!skipAuthRefresh && !isRefreshCall && !originalRequest?._retry) {
        originalRequest._retry = true;
        const outcome = await refreshSession();
        if (outcome === 'unavailable') {
          // Auth backend blip: keep the session, surface the error, retry later.
          return Promise.reject(error);
        }
        if (outcome === 'refreshed') {
          if (!USE_COOKIES) {
            const freshToken = await getAccessToken();
            if (freshToken) {
              originalRequest.headers = originalRequest.headers || {};
              originalRequest.headers.Authorization = `Bearer ${freshToken}`;
            }
          }
          return api(originalRequest);
        }
      }

      if (!originalRequest?._skipAuthClear) {
        await clearStoredTokens();
      }
      if (!suppressUnauthorized) {
        notifyUnauthorized();
      }
    }
    return Promise.reject(error);
  }
);

type CacheEntry<T> = { value: T | null; ts: number };

// BillingStatus is imported from AuthContext — single source of truth
import type { BillingStatus } from '../context/AuthContext';

export type SubscriptionSnapshot = {
  status: string;
  plan_type?: string;
  plan_tier?: string;
  current_period_start?: string | null;
  current_period_end?: string | null;
  next_billing_date?: string | null;
  cancel_at_period_end: boolean;
  cancel_at?: string | null;
  pending_plan_tier?: string | null;
  pending_billing_cycle?: string | null;
  pending_plan_effective_at?: string | null;
  amount?: number | null;
  currency: string;
};

type SubscriptionMutationResponse = {
  message: string;
  subscription: SubscriptionSnapshot;
  invoice_url?: string | null;
  invoice_id?: string | null;
  /** Plan change whose payment failed or needs authentication: nothing changed until invoice_url is paid. */
  payment_pending?: boolean;
};

/** GET /api/billing/change-plan/preview (BIL-L05). Amounts are in the smallest currency unit. */
export type PlanChangePreview = {
  change_type: 'upgrade' | 'downgrade' | 'same_plan';
  effective: 'now' | 'period_end' | 'none';
  plan_tier: string;
  billing_cycle: string;
  current_plan_tier: string;
  current_billing_cycle: string;
  currency: string;
  amount_due_now: number;
  effective_at?: string | null;
  renewal_date?: string | null;
  renewal_amount?: number | null;
  new_period_starts_now: boolean;
  trialing: boolean;
  cancels_scheduled_pause: boolean;
  /** Same plan only: confirming undoes a pending downgrade (BIL-L07) / a scheduled cancel (BIL-L09). */
  releases_pending_change?: boolean;
  pending_plan_tier?: string | null;
  clears_scheduled_cancel?: boolean;
  proration_date?: number | null;
};

// Single source of truth for the cancel-survey reason taxonomy. Shared by
// cancelSubscription (redundant fallback if the survey POST fails) and
// submitCancelSurvey (the primary write).
export type CancelSurveyReason =
  | 'too_expensive'
  | 'not_using'
  | 'missing_features'
  | 'switching'
  | 'temporary_break'
  | 'other';

export type BillingCheckoutConflictCode =
  | 'already_subscribed'
  | 'resume_available'
  | 'pending_cancellation_use_change_plan'
  | 'subscription_gone'
  | 'intro_offer_unavailable'
  | 'intro_offer_checkout_in_progress';

export type BillingCheckoutConflict = {
  code: BillingCheckoutConflictCode;
  detail: string;
  action?: 'resume' | 'change_plan' | 'checkout' | string;
  subscription_status?: string;
  current_plan_tier?: string;
  current_billing_cycle?: string;
  /** Seconds until the blocking reservation lapses server-side (sent with
   *  intro_offer_checkout_in_progress); retrying earlier is futile. */
  retry_after_seconds?: number;
};

export const getBillingCheckoutConflict = (error: any): BillingCheckoutConflict | null => {
  if (error?.response?.status !== 409) return null;
  const data = error?.response?.data;
  const candidate = data?.code ? data : data?.detail?.code ? data.detail : null;
  if (!candidate?.code) return null;
  if (
    candidate.code !== 'already_subscribed'
    && candidate.code !== 'resume_available'
    && candidate.code !== 'pending_cancellation_use_change_plan'
    && candidate.code !== 'subscription_gone'
    && candidate.code !== 'intro_offer_unavailable'
    && candidate.code !== 'intro_offer_checkout_in_progress'
  ) {
    return null;
  }
  return candidate as BillingCheckoutConflict;
};

// Paywall-abandonment discount ladder — "comeback" (promo: "comeback") and
// "final_offer" (promo: "final_offer") on checkout. The backend re-validates
// eligibility server-side for both and rejects with one of these codes when
// the client's optimistic offer doesn't hold up: 409 once already used, 400
// otherwise not eligible, or (final_offer only) 400 if a trial flag slipped
// through since the final offer is paid-only. All must be handled the same
// way on the client: drop the promo and retry checkout at full price — never
// surface this as a checkout failure.
export type PaywallOfferDeclineCode =
  | 'retention_offer_already_used'
  | 'retention_offer_not_eligible'
  | 'final_offer_requires_paid_checkout';

export type PaywallOfferDecline = {
  code: PaywallOfferDeclineCode;
  detail?: string;
};

export const getPaywallOfferDecline = (error: any): PaywallOfferDecline | null => {
  const status = error?.response?.status;
  if (status !== 400 && status !== 409) return null;
  const data = error?.response?.data;
  const candidate = data?.code ? data : data?.detail?.code ? data.detail : null;
  if (
    candidate?.code !== 'retention_offer_already_used'
    && candidate?.code !== 'retention_offer_not_eligible'
    && candidate?.code !== 'final_offer_requires_paid_checkout'
  ) {
    return null;
  }
  return candidate as PaywallOfferDecline;
};

export type OnboardingPath = 'credential_prep' | 'session_analysis' | 'mentor_review' | 'practice_first';

type CacheableGetConfig = NonNullable<Parameters<typeof api.get>[1]> & {
  _cacheMs?: number;
  _staleOnRateLimitMs?: number;
  _skipDedupe?: boolean;
};

const cachedGetResponses = new Map<string, { response: AxiosResponse<any>; ts: number }>();
const pendingCachedGets = new Map<string, Promise<AxiosResponse<any>>>();
const getRateLimitCooldowns = new Map<string, number>();

function stableStringify(value: any): string {
  if (value === null || value === undefined) return '';
  if (typeof value !== 'object') return String(value);
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(',')}]`;
  return `{${Object.keys(value)
    .sort()
    .map((key) => `${key}:${stableStringify(value[key])}`)
    .join(',')}}`;
}

function cachedGetKey(url: string, config?: CacheableGetConfig): string {
  const params = config?.params ? stableStringify(config.params) : '';
  const responseType = config?.responseType || 'json';
  return `${url}|${params}|${responseType}`;
}

function retryAfterMs(headerValue: unknown): number {
  return parseRetryAfterMs(headerValue) ?? 60_000;
}

function clearApiGetCache(prefix?: string) {
  if (!prefix) {
    cachedGetResponses.clear();
    pendingCachedGets.clear();
    getRateLimitCooldowns.clear();
    return;
  }

  for (const key of Array.from(cachedGetResponses.keys())) {
    if (key.startsWith(prefix)) cachedGetResponses.delete(key);
  }
  for (const key of Array.from(pendingCachedGets.keys())) {
    if (key.startsWith(prefix)) pendingCachedGets.delete(key);
  }
  for (const key of Array.from(getRateLimitCooldowns.keys())) {
    if (key.startsWith(prefix)) getRateLimitCooldowns.delete(key);
  }
}

async function cachedGet<T = any>(
  url: string,
  config: CacheableGetConfig = {}
): Promise<AxiosResponse<T>> {
  const {
    _cacheMs = 15_000,
    _staleOnRateLimitMs = 5 * 60_000,
    _skipDedupe,
    ...axiosConfig
  } = config;

  if (axiosConfig.responseType && axiosConfig.responseType !== 'json') {
    return api.get<T>(url, axiosConfig);
  }

  const key = cachedGetKey(url, config);
  const now = Date.now();
  const cached = cachedGetResponses.get(key);

  if (cached && now - cached.ts < _cacheMs) {
    return cached.response as AxiosResponse<T>;
  }

  const cooldownUntil = getRateLimitCooldowns.get(key);
  if (cooldownUntil && cooldownUntil > now && cached && now - cached.ts < _staleOnRateLimitMs) {
    return cached.response as AxiosResponse<T>;
  }

  if (!_skipDedupe) {
    const pending = pendingCachedGets.get(key);
    if (pending) {
      return pending as Promise<AxiosResponse<T>>;
    }
  }

  const request = api.get<T>(url, axiosConfig).then((response) => {
    cachedGetResponses.set(key, { response, ts: Date.now() });
    getRateLimitCooldowns.delete(key);
    return response;
  }).catch((error) => {
    if (error?.response?.status === 429) {
      const retryMs = retryAfterMs(error.response.headers?.['retry-after']);
      getRateLimitCooldowns.set(key, Date.now() + retryMs);
      const fallback = cachedGetResponses.get(key);
      if (fallback && Date.now() - fallback.ts < _staleOnRateLimitMs) {
        return fallback.response as AxiosResponse<T>;
      }
    }
    throw error;
  }).finally(() => {
    pendingCachedGets.delete(key);
  });

  pendingCachedGets.set(key, request);
  return request;
}

export type IntakeSubmissionPayload = {
  profile_type?: 'student' | 'coach' | 'mentor';
  onboarding_path?: OnboardingPath;
  credentials?: string;
  coaching_hours?: number;
  coaching_education?: string;
  current_credential?: 'none' | 'acc' | 'pcc' | 'mcc' | 'other';
  primary_goal?: string;
  focus_competency?: 'C1' | 'C2' | 'C3' | 'C4' | 'C5' | 'C6' | 'C7' | 'C8';
  coaching_frequency?: 'daily' | 'weekly' | 'monthly' | 'rarely' | 'not_yet' | `mentor_volume:${'none_yet' | 'few' | 'several' | 'many'}`;
  biggest_challenge?: 'listening' | 'questions' | 'presence' | 'structure' | 'confidence' | 'evidence' | 'calibration' | 'time' | 'difficult_feedback' | 'other';
  language?: string;
  generate_suggestions?: boolean;
  skip?: boolean;
};

// Simple in-memory caching to reduce rate-limited calls during navigation churn
let currentUserCache: CacheEntry<User> = { value: null, ts: 0 };
let currentUserPromise: Promise<User> | null = null;
const USER_TTL_MS = 30_000; // 30s

let billingStatusCache: CacheEntry<BillingStatus> = { value: null, ts: 0 };
let billingStatusPromise: Promise<BillingStatus> | null = null;
const BILLING_TTL_MS = 15_000; // 15s
let billingRefreshLastTs = 0;
let billingRefreshPromise: Promise<BillingStatus> | null = null;
const BILLING_REFRESH_TTL_MS = 60_000; // 1 min

export function invalidateBillingCaches(): void {
  clearApiGetCache('/api/billing/status');
  clearApiGetCache('/api/user-profile/profile');
  billingStatusCache = { value: null, ts: 0 };
  billingStatusPromise = null;
  billingRefreshLastTs = 0;
}

export function invalidateAuthAndBillingCaches(): void {
  clearApiGetCache('/api/auth/me');
  currentUserCache = { value: null, ts: 0 };
  currentUserPromise = null;
  invalidateBillingCaches();
}

type ForceRefreshOptions = { force?: boolean };
type MagicLinkAttribution = Record<string, string | null | undefined>;

// Auth API
export const authAPI = {
  loginWithGoogleIdToken: async (idToken: string): Promise<AuthResponse> => {
    const response = await api.post<AuthResponse>('/api/auth/oauth2/google', {
      id_token: idToken,
    });
    return await handleAuthResponse(response);
  },

  // Passwordless auth — magic link (sign-up + login) and OTP (login).
  // Both ``request`` endpoints always succeed (always-200) so the client
  // can't distinguish "email not registered" from "email exists" — that's
  // an enumeration-prevention guarantee from the backend.
  requestMagicLink: async (
    email: string,
    opts?: {
      intent?: 'login' | 'signup';
      language?: string;
      full_name?: string;
      attribution?: MagicLinkAttribution;
      /** Landing plan choice, echoed on the emailed link (see planContext). */
      plan?: string;
      cycle?: string;
    }
  ): Promise<{ ok: true }> => {
    const response = await api.post<{ ok: true }>('/api/auth/magic-link/request', {
      email,
      intent: opts?.intent || 'login',
      language: opts?.language || 'en',
      full_name: opts?.full_name,
      attribution: opts?.attribution,
      plan: opts?.plan,
      cycle: opts?.cycle,
    });
    return response.data;
  },

  consumeMagicLink: async (token: string): Promise<AuthResponse & { is_new_account?: boolean }> => {
    const response = await api.post<AuthResponse & { is_new_account?: boolean }>(
      '/api/auth/magic-link/consume',
      { token },
      {
        _suppressUnauthorized: true,
        _skipAuthClear: true,
        _skipAuthRefresh: true,
      } as any
    );
    return await handleAuthResponse(response);
  },

  requestOtp: async (email: string, language?: string): Promise<{ ok: true; sent?: boolean; retry_after_seconds?: number }> => {
    const response = await api.post<{ ok: true; sent?: boolean; retry_after_seconds?: number }>('/api/auth/otp/request', {
      email,
      language: language || 'en',
    });
    return response.data;
  },

  verifyOtp: async (email: string, code: string): Promise<AuthResponse & { is_new_account?: boolean }> => {
    const response = await api.post<AuthResponse & { is_new_account?: boolean }>(
      '/api/auth/otp/verify',
      { email, code },
      {
        _suppressUnauthorized: true,
        _skipAuthClear: true,
        _skipAuthRefresh: true,
      } as any
    );
    return await handleAuthResponse(response);
  },

  // One-shot dismiss for the "we've moved to passwordless" banner. Idempotent
  // server-side; the first acknowledgement timestamp is preserved.
  acknowledgePasswordlessMigration: async (): Promise<{ ok: true; acknowledged_at: string }> => {
    const response = await api.post<{ ok: true; acknowledged_at: string }>(
      '/api/auth/passwordless-migration/acknowledge'
    );
    return response.data;
  },

  // Guided tour (QA F4): finishing or skipping ends the auto-start on every
  // device. Idempotent server-side; the first timestamp is kept.
  markGuidedTourSeen: async (
    outcome: 'completed' | 'skipped',
  ): Promise<{ ok: true; guided_tour_seen_at: string }> => {
    const response = await api.post<{ ok: true; guided_tour_seen_at: string }>(
      '/api/auth/guided-tour/seen',
      { outcome },
    );
    currentUserCache = { value: null, ts: 0 };
    currentUserPromise = null;
    return response.data;
  },

  dismissGuidanceHint: async (hintId: string): Promise<{ ok: true; dismissed_hints: string[] }> => {
    const response = await api.post<{ ok: true; dismissed_hints: string[] }>(
      `/api/auth/hints/${encodeURIComponent(hintId)}/dismiss`,
    );
    currentUserCache = { value: null, ts: 0 };
    currentUserPromise = null;
    return response.data;
  },

  getCurrentUser: async (options?: ForceRefreshOptions): Promise<User> => {
    const now = Date.now();
    if (options?.force) {
      currentUserCache = { value: null, ts: 0 };
      currentUserPromise = null;
      clearApiGetCache('/api/auth/me');
    }
    if (!options?.force && currentUserCache.value && now - currentUserCache.ts < USER_TTL_MS) {
      return currentUserCache.value;
    }
    if (currentUserPromise) return currentUserPromise;
    console.log('👤 Checking current user...');
    currentUserPromise = (async () => {
      try {
        const response = await api.get<User>('/api/auth/me', {
          _suppressUnauthorized: true,
        } as any);
        currentUserCache = { value: response.data, ts: Date.now() };
        console.log('✅ getCurrentUser successful');
        return response.data;
      } catch (error: any) {
        // Rate-limited: the account we loaded moments ago is still good (same as billing status).
        if (error?.response?.status === 429 && currentUserCache.value) {
          return currentUserCache.value;
        }
        throw error;
      } finally {
        currentUserPromise = null;
      }
    })();
    return currentUserPromise;
  },

  getIntakeSummary: async (): Promise<IntakeSummary> => {
    const response = await cachedGet<IntakeSummary>('/api/auth/onboarding/intake', {
      _cacheMs: 60_000,
      _staleOnRateLimitMs: 10 * 60_000,
    });
    return response.data;
  },

  submitIntake: async (payload: IntakeSubmissionPayload): Promise<IntakeSummary> => {
    const response = await api.post<IntakeSummary>('/api/auth/onboarding/intake', payload);
    clearApiGetCache();
    billingStatusCache = { value: null, ts: 0 };
    currentUserCache = { value: null, ts: 0 };
    return response.data;
  },

  skipIntake: async (): Promise<IntakeSummary> => {
    const response = await api.post<IntakeSummary>('/api/auth/onboarding/intake', { skip: true });
    clearApiGetCache();
    billingStatusCache = { value: null, ts: 0 };
    currentUserCache = { value: null, ts: 0 };
    return response.data;
  },

  regenerateIntake: async (language?: 'en' | 'tr'): Promise<IntakeSummary> => {
    const qs = language ? `?language=${encodeURIComponent(language)}` : '';
    const response = await api.post<IntakeSummary>(
      `/api/auth/onboarding/intake/regenerate${qs}`,
    );
    clearApiGetCache('/api/auth/onboarding/intake');
    return response.data;
  },

  generateDevelopmentPlan: async (language?: 'en' | 'tr'): Promise<IntakeSummary> => {
    const qs = language ? `?language=${encodeURIComponent(language)}` : '';
    const response = await api.post<IntakeSummary>(
      `/api/auth/onboarding/plan/generate${qs}`,
    );
    clearApiGetCache('/api/auth/onboarding/intake');
    return response.data;
  },

  completeOnboarding: async (): Promise<void> => {
    await api.post('/api/auth/onboarding/complete', {});
    clearApiGetCache();
    currentUserCache = { value: null, ts: 0 };
    billingStatusCache = { value: null, ts: 0 };
  },

  logout: async (): Promise<void> => {
    console.log('🚪 Starting logout process...');
    try {
      await api.post('/api/auth/logout', {});
    } catch {
      // Ignore
    }
    await clearStoredTokens();
    console.log('✅ Logout complete - token removed');
    // Clear caches
    clearApiGetCache();
    currentUserCache = { value: null, ts: 0 };
    billingStatusCache = { value: null, ts: 0 };
    // No need to make API calls - token removal is sufficient
    // The next auth check will fail automatically
  },
};

// Billing API
export const billingAPI = {
  checkout: async (
    planType: 'b2c' | 'b2b2c',
    planTier: 'tier1' | 'tier2' | 'tier3',
    billingCycle: '1mo' | '3mo' | '6mo' = '1mo',
    quantity?: number,
    uiMode?: 'hosted' | 'embedded',
    options?: { startPaidTrial?: boolean; trialPeriodDays?: number; promo?: 'comeback' | 'final_offer' },
  ): Promise<{ checkout_url?: string; client_secret?: string; session_id?: string; currency?: string }> => {
    const payload: Record<string, any> = {
      plan_type: planType,
      plan_tier: planTier,
      billing_cycle: billingCycle,
    };
    if (typeof quantity === 'number') {
      payload.quantity = quantity;
    }
    if (uiMode) {
      payload.ui_mode = uiMode;
    }
    if (options?.startPaidTrial) {
      payload.start_paid_trial = true;
    }
    if (typeof options?.trialPeriodDays === 'number') {
      payload.trial_period_days = options.trialPeriodDays;
    }
    // Paywall-abandonment discount. Ignored server-side when combined with
    // start_paid_trial, and re-validated regardless — see
    // getPaywallOfferDecline() for how callers must handle rejection.
    if (options?.promo) {
      payload.promo = options.promo;
    }
    try {
      const response = await api.post('/api/billing/checkout', payload);
      return response.data;
    } catch (error: any) {
      const conflict = getBillingCheckoutConflict(error);
      if (conflict) {
        error.billingCheckoutConflict = conflict;
      }
      throw error;
    }
  },
  schoolPlanPreview: async (schoolId: number, seatTarget?: number): Promise<SchoolPlanPreview> => {
    const params: Record<string, number | string> = {};
    if (typeof seatTarget === 'number') {
      params.seat_target = seatTarget;
    }
    const response = await api.get<SchoolPlanPreview>(`/api/billing/schools/${schoolId}/plan-preview`, {
      params: Object.keys(params).length ? params : undefined,
    });
    return response.data;
  },
  schoolPlanCheckout: async (schoolId: number, seatTarget?: number): Promise<{ checkout_url: string; seat_target?: number }> => {
    const payload = typeof seatTarget === 'number' ? { seat_target: seatTarget } : {};
    const response = await api.post<{ checkout_url: string; seat_target?: number }>(`/api/billing/schools/${schoolId}/plan-checkout`, payload);
    return response.data;
  },
  status: async (options?: ForceRefreshOptions): Promise<BillingStatus> => {
    const now = Date.now();
    if (options?.force) {
      billingStatusCache = { value: null, ts: 0 };
      billingStatusPromise = null;
      clearApiGetCache('/api/billing/status');
    }
    if (!options?.force && billingStatusCache.value && now - billingStatusCache.ts < BILLING_TTL_MS) {
      return billingStatusCache.value;
    }
    if (billingStatusPromise) return billingStatusPromise;
    billingStatusPromise = (async () => {
      try {
        const response = await api.get<BillingStatus>('/api/billing/status');
        billingStatusCache = { value: response.data, ts: Date.now() };
        return response.data;
      } finally {
        billingStatusPromise = null;
      }
    })().catch((err) => {
      const status = err?.response?.status;
      // Gracefully back off on rate limits to avoid redirect loops
      if (status === 429 && billingStatusCache.value) {
        const fallback = billingStatusCache.value;
        billingStatusCache = { value: fallback, ts: Date.now() + BILLING_TTL_MS }; // short cooldown
        return fallback;
      }
      throw err;
    });
    return billingStatusPromise;
  },
  portal: async (): Promise<{ url: string }> => {
    const response = await api.post('/api/billing/portal', {});
    return response.data as any;
  },
  changePlan: async (
    planTier: 'tier1' | 'tier2' | 'tier3',
    billingCycle: '1mo' | '3mo' | '6mo' = '1mo',
    prorationBehavior: 'create_prorations' | 'none' | 'always_invoice' = 'always_invoice',
    /** From changePlanPreview, so the charge equals the previewed amount. */
    prorationDate?: number | null,
  ): Promise<SubscriptionMutationResponse> => {
    const response = await api.post<SubscriptionMutationResponse>('/api/billing/change-plan', {
      plan_tier: planTier,
      billing_cycle: billingCycle,
      proration_behavior: prorationBehavior,
      ...(typeof prorationDate === 'number' ? { proration_date: prorationDate } : {}),
    });
    clearApiGetCache();
    // Also resets the refresh throttle so the next refresh reads Stripe (BILL-24).
    invalidateBillingCaches();
    return response.data;
  },
  /** What a plan change costs today and when it applies; changes nothing (BIL-L05). */
  changePlanPreview: async (
    planTier: 'tier1' | 'tier2' | 'tier3',
    billingCycle: '1mo' | '3mo' | '6mo' = '1mo',
  ): Promise<PlanChangePreview> => {
    const response = await api.get<PlanChangePreview>('/api/billing/change-plan/preview', {
      params: { plan_tier: planTier, billing_cycle: billingCycle },
      // A slow Stripe preview must not hold the dialog: the caller falls back to generic copy.
      timeout: 10_000,
    });
    return response.data;
  },
  cancelSubscription: async (
    terminateNow = false,
    // Redundant fallback only — the cancel-survey POST (fired separately,
    // fire-and-forget) is the primary write. Sent here too so the reason
    // survives even if that earlier request failed (flaky network/5xx).
    // Backend prefers the survey row when one exists.
    reason?: CancelSurveyReason,
    reasonNote?: string,
  ): Promise<SubscriptionMutationResponse> => {
    const response = await api.post<SubscriptionMutationResponse>('/api/billing/cancel', {
      terminate_now: terminateNow,
      reason,
      reason_note: reasonNote?.trim() || undefined,
    });
    clearApiGetCache();
    billingStatusCache = { value: null, ts: 0 };
    return response.data;
  },
  applyRetentionOffer: async (): Promise<SubscriptionMutationResponse> => {
    const response = await api.post<SubscriptionMutationResponse>('/api/billing/apply-retention-offer', {});
    clearApiGetCache();
    billingStatusCache = { value: null, ts: 0 };
    return response.data;
  },
  firstMonthDiscount: async (): Promise<SubscriptionMutationResponse> => {
    const response = await api.post<SubscriptionMutationResponse>('/api/billing/retention/first-month-discount', {});
    clearApiGetCache();
    billingStatusCache = { value: null, ts: 0 };
    return response.data;
  },
  pauseSubscription: async (): Promise<SubscriptionMutationResponse> => {
    const response = await api.post<SubscriptionMutationResponse>('/api/billing/retention/pause', {});
    clearApiGetCache();
    billingStatusCache = { value: null, ts: 0 };
    return response.data;
  },
  submitCancelSurvey: async (params: {
    reason: CancelSurveyReason;
    reason_note?: string;
  }): Promise<{ success?: boolean; message?: string }> => {
    const response = await api.post<{ success?: boolean; message?: string }>('/api/billing/cancel-survey', {
      reason: params.reason,
      reason_note: params.reason_note?.trim() || undefined,
    });
    return response.data;
  },
  /** EU withdrawal (BIL-L23): ends the subscription now; the refund is issued by support. */
  withdrawFromContract: async (): Promise<SubscriptionMutationResponse> => {
    const response = await api.post<SubscriptionMutationResponse>('/api/billing/withdraw', {});
    clearApiGetCache();
    invalidateBillingCaches();
    return response.data;
  },
  resumeSubscription: async (): Promise<SubscriptionMutationResponse> => {
    const response = await api.post<SubscriptionMutationResponse>('/api/billing/resume');
    clearApiGetCache();
    // Also resets the refresh throttle so the next refresh reads Stripe (BILL-24).
    invalidateBillingCaches();
    return response.data;
  },
  pricing: async (): Promise<{
    currency: string;
    country?: string | null;
    tiers: {
      tier: 'tier1' | 'tier2' | 'tier3';
      recommended?: boolean;
      analyses_per_month?: number;
      transcription_minutes_per_month?: number;
      intervals?: Array<{
        cycle: '1mo' | '3mo' | '6mo';
        months: number;
        eur: { cents: number; price_id: string };
        usd: { cents: number; price_id: string };
        try: { cents: number; price_id: string };
      }>;
    }[];
    topups?: Array<{
      key: string;
      analyses: number;
      minutes: number;
      eur: { cents: number; price_id: string };
      usd: { cents: number; price_id: string };
      try: { cents: number; price_id: string };
    }>;
    source?: string | null;
    publishable_key?: string | null;
  }> => {
    const response = await api.get('/api/billing/pricing');
    const data = response.data;
    // Backend returns `tiers` as a dict keyed by tier id
    // ({ tier1: {...}, tier2: {...} }), but every frontend consumer treats it
    // as an array of { tier, ...}. Normalize here so a plain object never
    // reaches a `for...of` / `.find` / `.forEach` call (which would throw and
    // leave pricing blank — all cards rendering "—"). Tolerates the legacy
    // array shape too.
    if (data?.tiers && !Array.isArray(data.tiers)) {
      data.tiers = Object.entries(data.tiers).map(([tier, value]) => ({
        tier,
        ...(value as Record<string, unknown>),
      }));
    }
    // Cache publishable key for embedded checkout initialization
    if (data?.publishable_key) {
      billingAPI._stripePublishableKey = data.publishable_key;
    }
    return data;
  },
  _stripePublishableKey: null as string | null,
  // Throttled refresh to avoid Stripe/limiter spam (and 429 loops).
  // Forced requests follow weaker in-flight work so stale responses cannot win.
  refresh: async (options?: ForceRefreshOptions): Promise<BillingStatus> => {
    const now = Date.now();
    const activeRefresh = billingRefreshPromise;
    clearApiGetCache('/api/user-profile/profile');
    if (options?.force) {
      invalidateBillingCaches();
    }
    if (activeRefresh && !options?.force) return activeRefresh;
    if (
      !options?.force
      && billingRefreshLastTs
      && now - billingRefreshLastTs < BILLING_REFRESH_TTL_MS
    ) {
      // Within cooldown: return cached status to keep UX flowing.
      return await billingAPI.status();
    }

    const task = (async () => {
      if (activeRefresh) {
        try {
          await activeRefresh;
        } catch {}
      }
      const response = await api.post<BillingStatus>('/api/billing/refresh', {});
      billingStatusCache = { value: null, ts: 0 };
      billingRefreshLastTs = Date.now();
      return response.data;
    })();
    billingRefreshPromise = task;
    try {
      return await task;
    } finally {
      if (billingRefreshPromise === task) {
        billingRefreshPromise = null;
      }
    }
  },

  acceptTerms: async (): Promise<void> => {
    await api.post('/api/billing/accept-terms', {});
    // Bust cached status after accepting terms so UI immediately advances
    clearApiGetCache();
    billingStatusCache = { value: null, ts: 0 };
    currentUserCache = { value: null, ts: 0 };
  },
};

// Credits API
export const creditsAPI = {
  getStatus: async (): Promise<CreditStatus> => {
    const response = await cachedGet<CreditStatus>('/api/credits/status', {
      _cacheMs: 30_000,
      _staleOnRateLimitMs: 10 * 60_000,
    });
    return response.data;
  },
  getPacks: async (): Promise<CreditPackList> => {
    const response = await api.get<CreditPackList>('/api/credits/packs');
    return response.data;
  },
  checkout: async (
    packId: number,
    schoolId?: number,
    redirectUrl?: string,
    lang?: string,
  ): Promise<{ checkout_url: string; session_id: string; currency: string; price_cents?: number | null }> => {
    const payload: Record<string, any> = { pack_id: packId };
    if (typeof schoolId === 'number') {
      payload.school_id = schoolId;
    }
    if (redirectUrl) {
      payload.redirect_url = redirectUrl;
    }
    if (lang) {
      payload.lang = lang;
    }
    const response = await api.post('/api/credits/checkout', payload);
    return response.data as any;
  },
  getSchoolStatus: async (schoolId: number): Promise<any> => {
    const response = await api.get(`/api/credits/school/${schoolId}/status`);
    return response.data as any;
  },
  getSchoolUsage: async (schoolId: number): Promise<any> => {
    const response = await api.get(`/api/credits/school/${schoolId}/usage`);
    return response.data as any;
  },
  adminAdjust: async (payload: { user_id?: number; school_id?: number; delta: number; credit_type: 'subscription' | 'purchased'; note?: string }): Promise<any> => {
    const response = await api.post('/api/credits/admin/adjust', payload);
    return response.data as any;
  },
};

// Admin RAG Knowledge Base API
export const adminRAGAPI = {
  uploadKnowledge: async (
    file: File | { uri: string; type: string; name: string },
    coachingMethod?: string,
    contentType?: string
  ): Promise<{ message: string; document_id: number; chunks_created: number }> => {
    const formData = new FormData();

    if ('uri' in file) {
      // Handle Expo file (data URI, blob URI, or native file URI)
      if (file.uri.startsWith('data:') || file.uri.startsWith('blob:')) {
        const response = await fetch(file.uri);
        const blob = await response.blob();
        formData.append('file', blob, file.name || 'document');
      } else {
        // React Native: pass the file via FormData using its URI (no Base64 decode)
        formData.append(
          'file',
          {
            uri: file.uri,
            name: file.name || 'document',
            type: file.type || 'application/octet-stream',
          } as any,
        );
      }
    } else {
      formData.append('file', file);
    }

    if (coachingMethod) formData.append('coaching_method', coachingMethod);
    if (contentType) formData.append('content_type', contentType);

    const useCookies = USE_COOKIES;
    const token = useCookies ? null : await getAccessToken();
    const fetchResponse = await fetch(`${API_BASE_URL}/api/documents/admin/rag-knowledge/upload`, {
      method: 'POST',
      headers: {
        ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
      },
      credentials: useCookies ? 'include' : 'same-origin',
      body: formData,
    });

    if (!fetchResponse.ok) {
      const errorData = await fetchResponse.json().catch(() => ({ detail: 'Upload failed' }));
      throw new Error(errorData.detail || `HTTP ${fetchResponse.status}`);
    }

    const response = { data: await fetchResponse.json() };
    return response.data;
  },

  listKnowledge: async (): Promise<Document[]> => {
    const response = await api.get<Document[]>('/api/documents/admin/rag-knowledge');
    return response.data;
  },

  deleteKnowledge: async (documentId: number): Promise<{ message: string }> => {
    const response = await api.delete(`/api/documents/admin/rag-knowledge/${documentId}`);
    return response.data;
  },

  getStats: async (): Promise<CollectionStats> => {
    const response = await api.get<CollectionStats>('/api/documents/admin/stats');
    return response.data;
  },

  cleanupOrphanedChunks: async (): Promise<{ message: string }> => {
    const response = await api.post('/api/documents/admin/cleanup-orphaned-chunks');
    return response.data;
  },

  getVectorHealth: async (): Promise<VectorStoreHealth> => {
    const response = await api.get<VectorStoreHealth>('/api/documents/admin/vector-health');
    return response.data;
  },
};

/** GET /api/documents/admin/vector-health (pgvector readiness, counts only). */
export interface VectorStoreHealth {
  status: 'ok' | 'error';
  storage: string;
  pgvector_version?: string | null;
  total_chunks?: number;
  embedded_chunks?: number;
  error?: string;
}

// Super-admin self-service QA helpers
export interface AdminOnboardingResetResponse {
  onboarding_completed: boolean;
  intake_completed: boolean;
  intake_profile_cleared: boolean;
  development_plan_versions_deleted: number;
}

export const adminOnboardingAPI = {
  // Resets ONLY the calling super admin's own onboarding/intake state so the
  // onboarding flow can be re-run for QA on the testing environment.
  resetOwnOnboarding: async (): Promise<AdminOnboardingResetResponse> => {
    const response = await api.post<AdminOnboardingResetResponse>('/api/admin/onboarding/reset');
    invalidateAuthAndBillingCaches();
    return response.data;
  },
};

// Unified AI API. Failed calls throw apiErrorFromBody errors that keep the
// HTTP status (and any structured detail), so callers pick a translated
// message instead of the backend's English `detail` (CHAT-2 / CHAT-19).
export const aiAPI = {
  sendMessage: async (payload: {
    message?: string;
    sessionId?: number;
    file?: File | { uri: string; type: string; name: string };
    documentId?: number;
    analysisInstructions?: string;
    includeInICFProgress?: boolean;
    forceIntent?: 'chat' | 'analyze' | 'hybrid' | 'clarify';
    toolResults?: ToolResult[];
    language?: string;
    analysisMode?: string;
    requestedLevel?: string;
    includeMarkerTable?: boolean;
    customPrompt?: string;
    engagementId?: number;
    /** One key per user intent; reused on the 401-refresh retry below. */
    idempotencyKey?: string;
  }): Promise<UnifiedAIResponse> => {
    const formData = new FormData();
    // ASYNC-6: analysis requests (and chat messages the caller keys, which
    // the server may turn into a billed re-assessment) carry an
    // Idempotency-Key so a retried request returns the existing job instead
    // of starting (and charging for) a second one.
    const isAnalysisRequest = Boolean(payload.file) || payload.documentId !== undefined;
    const idempotencyHeaders = messageIdempotencyHeaders({
      isAnalysisRequest,
      idempotencyKey: payload.idempotencyKey,
    });

    if (payload.message !== undefined) {
      formData.append('message', payload.message);
    }
    if (payload.sessionId) {
      formData.append('session_id', payload.sessionId.toString());
    }
    if (payload.documentId) {
      formData.append('document_id', payload.documentId.toString());
    }
    if (payload.analysisInstructions) {
      formData.append('analysis_instructions', payload.analysisInstructions);
    }
    if (payload.includeInICFProgress !== undefined) {
      formData.append('include_in_icf_progress', payload.includeInICFProgress.toString());
    }
    if (payload.forceIntent) {
      formData.append('force_intent', payload.forceIntent);
    }
    if (payload.toolResults && payload.toolResults.length > 0) {
      formData.append('tool_results', JSON.stringify(payload.toolResults));
    }
    if (payload.language) {
      formData.append('language', payload.language);
    }
    if (payload.analysisMode) {
      formData.append('analysis_mode', payload.analysisMode);
    }
    if (payload.requestedLevel) {
      formData.append('requested_level', payload.requestedLevel);
    }
    if (payload.includeMarkerTable !== undefined) {
      formData.append('include_marker_table', payload.includeMarkerTable.toString());
    }
    if (payload.customPrompt) {
      formData.append('custom_prompt', payload.customPrompt);
    }
    if (payload.engagementId !== undefined) {
      formData.append('engagement_id', payload.engagementId.toString());
    }

    if (payload.file) {
      const file = payload.file;
      if ('uri' in file) {
        if (file.uri.startsWith('data:') || file.uri.startsWith('blob:')) {
          const response = await fetch(file.uri);
          const blob = await response.blob();
          formData.append('file', blob, file.name || 'document');
        } else {
          formData.append(
            'file',
            {
              uri: file.uri,
              name: file.name || 'document',
              type: file.type || 'application/octet-stream',
            } as any,
          );
        }
      } else {
        formData.append('file', file);
      }
    }

    const useCookies2 = USE_COOKIES;
    const token2 = useCookies2 ? null : await getAccessToken();
    const fetchResponse = await fetch(`${API_BASE_URL}/api/ai/message`, {
      method: 'POST',
      headers: {
        ...(token2 ? { Authorization: `Bearer ${token2}` } : {}),
        ...idempotencyHeaders,
      },
      credentials: useCookies2 ? 'include' : 'same-origin',
      body: formData,
    });

    if (!fetchResponse.ok) {
      if (fetchResponse.status === 401) {
        const outcome = await refreshSession();
        if (outcome === 'unavailable') {
          throw apiErrorFromBody({ detail: 'Service temporarily unavailable' }, 503, 'HTTP 503');
        }
        if (outcome === 'refreshed') {
          const freshToken = USE_COOKIES ? null : await getAccessToken();
          const retryResponse = await fetch(`${API_BASE_URL}/api/ai/message`, {
            method: 'POST',
            headers: {
              ...(freshToken ? { Authorization: `Bearer ${freshToken}` } : {}),
              ...idempotencyHeaders,
            },
            credentials: USE_COOKIES ? 'include' : 'same-origin',
            body: formData,
          });
          if (!retryResponse.ok) {
            const errorData = await retryResponse.json().catch(() => ({ detail: 'Request failed' }));
            throw apiErrorFromBody(errorData, retryResponse.status, `HTTP ${retryResponse.status}`);
          }
          const retryData = await retryResponse.json();
          clearApiGetCache('/api/chat/sessions');
          clearApiGetCache('/api/user-documents/');
          clearApiGetCache('/api/transcriptions/');
          return retryData;
        }
        await clearStoredTokens();
        notifyUnauthorized();
        throw new Error('Unauthorized');
      }
      const errorData = await fetchResponse.json().catch(() => ({ detail: 'Request failed' }));
      throw apiErrorFromBody(errorData, fetchResponse.status, `HTTP ${fetchResponse.status}`);
    }

    const response = { data: await fetchResponse.json() };
    clearApiGetCache('/api/chat/sessions');
    clearApiGetCache('/api/user-documents/');
    clearApiGetCache('/api/transcriptions/');
    return response.data;
  },
};

// Documents API (for regular users - only document analysis via chat)
export const documentsAPI = {
  // Note: Regular users can no longer upload/manage documents
  // They can only analyze documents via chat interface

  analyze: async (
    file: File | { uri: string; type: string; name: string },
    options: Partial<AnalysisOptions> & { sessionId?: number; toolResults?: ToolResult[]; forceIntent?: 'chat' | 'analyze' | 'hybrid' | 'clarify' } = {}
  ): Promise<AsyncAnalysisResponse> => {
    const response = await aiAPI.sendMessage({
      file,
      analysisInstructions: options.analysisInstructions || options.customPrompt,
      sessionId: options.sessionId,
      includeInICFProgress: options.includeInIcfProgress,
      forceIntent: options.forceIntent || 'analyze',
      toolResults: options.toolResults,
      language: options.language,
      analysisMode: options.analysisMode,
      requestedLevel: options.requestedLevel,
      includeMarkerTable: options.includeMarkerTable,
      customPrompt: options.customPrompt,
      engagementId: options.engagementId,
      idempotencyKey: options.idempotencyKey,
    });
    return response as AsyncAnalysisResponse;
  },

  exportTable: async (data: TableExportRequest): Promise<Blob> => {
    const response = await api.post('/api/documents/export_table', data, {
      responseType: 'blob',
    });
    return response.data;
  },
};

// Task API for monitoring async operations
export const taskAPI = {
  getStatus: async (taskId: string): Promise<TaskStatus> => {
    const response = await api.get<TaskStatus>(`/api/tasks/status/${taskId}`);
    return response.data;
  },

  getResult: async (taskId: string): Promise<{ task_id: string; status: string; result?: ChatResponse; error?: string }> => {
    const response = await api.get(`/api/tasks/result/${taskId}`);
    return response.data;
  },

  cancel: async (taskId: string): Promise<{ message: string; task_id: string }> => {
    const response = await api.delete(`/api/tasks/cancel/${taskId}`);
    return response.data;
  },

  pollForCompletion: async (
    taskId: string,
    onProgress?: (status: TaskStatus) => void,
    maxAttempts: number = 150, // Kept for backward compatibility
    intervalMs: number = 2000
  ): Promise<UnifiedAIResponse> => {
    const inactivityTimeoutMs = Math.max(900_000, maxAttempts * intervalMs);
    let lastActivityAt = Date.now();

    while (true) {
      try {
        const status = await taskAPI.getStatus(taskId);
        lastActivityAt = Date.now();

        if (onProgress) {
          onProgress(status);
        }

        if (status.status === 'SUCCESS' && status.result) {
          return status.result as UnifiedAIResponse;
        }

        if (status.status === 'FAILURE') {
          throw new Error(status.error || 'Task failed');
        }

        if (status.status === 'REVOKED') {
          throw new Error(status.error || 'Task cancelled');
        }
      } catch (error) {
        if (Date.now() - lastActivityAt > inactivityTimeoutMs) {
          throw new Error('Task polling stalled - no status activity');
        }
        // Continue retrying for transient errors while activity timeout window remains.
      }

      await new Promise(resolve => setTimeout(resolve, intervalMs));
      if (Date.now() - lastActivityAt > inactivityTimeoutMs) {
        throw new Error('Task polling stalled - no status activity');
      }
    }
  },
};

// Chat API
export const chatAPI = {
  sendMessage: async (request: ChatRequest): Promise<AsyncChatResponse> => {
    const response = await aiAPI.sendMessage({
      message: request.message,
      sessionId: request.session_id,
      forceIntent: request.force_intent,
      language: request.language,
      idempotencyKey: request.idempotency_key,
    });
    return response as AsyncChatResponse;
  },

  sendLegacyMessage: async (request: ChatRequest): Promise<AsyncChatResponse> => {
    const response = await api.post<AsyncChatResponse>('/api/chat/send', {
      message: request.message,
      session_id: request.session_id,
      system_prompt: request.system_prompt,
      session_type: request.session_type,
      session_title: request.session_title,
      filters: request.filters,
      language: request.language,
    });
    clearApiGetCache('/api/chat/sessions');
    return response.data;
  },

  sendLegacyMessageWithPolling: async (
    request: ChatRequest,
    onProgress?: (status: TaskStatus) => void
  ): Promise<UnifiedAIResponse> => {
    const asyncResponse = await chatAPI.sendLegacyMessage(request);
    return await taskAPI.pollForCompletion(asyncResponse.task_id, onProgress);
  },

  getSessions: async (): Promise<ChatSession[]> => {
    const response = await cachedGet<ChatSession[]>('/api/chat/sessions', {
      _cacheMs: 10_000,
      _staleOnRateLimitMs: 60_000,
    });
    return response.data;
  },

  getMessages: async (sessionId: number): Promise<ChatMessage[]> => {
    const response = await api.get<ChatMessage[]>(`/api/chat/sessions/${sessionId}/messages`);
    return response.data;
  },

  renameSession: async (sessionId: number, title: string): Promise<ChatSession> => {
    const response = await api.patch<ChatSession>(`/api/chat/sessions/${sessionId}`, { title });
    clearApiGetCache('/api/chat/sessions');
    return response.data;
  },
  deleteSession: async (sessionId: number): Promise<{ message: string }> => {
    const response = await api.delete(`/api/chat/sessions/${sessionId}`);
    clearApiGetCache('/api/chat/sessions');
    return response.data;
  },
  createSession: async (title?: string): Promise<ChatSession> => {
    const response = await api.post<ChatSession>(`/api/chat/sessions`, { title });
    clearApiGetCache('/api/chat/sessions');
    return response.data;
  },

  /**
   * Export a chat session (analysis) as a PDF.
   * Returns a Blob that can be downloaded or shared.
   */
  exportSessionPdf: async (
    sessionId: number,
    language?: string,
    reportType?: 'standard' | 'observation',
  ): Promise<Blob> => {
    const params: Record<string, string> = {};
    if (language) params.language = language;
    if (reportType) params.type = reportType;
    const response = await api.get(
      `/api/chat/sessions/${sessionId}/export/pdf`,
      {
        params: Object.keys(params).length ? params : undefined,
        responseType: 'blob',
      }
    );
    return response.data;
  },

  /**
   * Attach a reference file to a chat session as hidden context.
   * The AI will see this file's text on every chat turn within the session.
   * Does NOT trigger the analysis pipeline.
   */
  attachContextFile: async (
    sessionId: number,
    file: { uri: string; name: string; type?: string } | File
  ): Promise<{
    message_id: number;
    filename: string;
    char_count: number;
    truncated: boolean;
    total_chars: number;
  }> => {
    const formData = new FormData();
    if (file instanceof File) {
      formData.append('file', file);
    } else {
      formData.append('file', {
        uri: file.uri,
        name: file.name,
        type: file.type || 'application/octet-stream',
      } as any);
    }

    const response = await api.post(
      `/api/chat/sessions/${sessionId}/attach-context`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data;
  },

  /** List reference files attached to a session. */
  listContextFiles: async (
    sessionId: number
  ): Promise<{
    items: Array<{
      message_id: number;
      filename: string;
      char_count: number;
      created_at: string;
    }>;
    total_chars: number;
  }> => {
    const response = await api.get(
      `/api/chat/sessions/${sessionId}/context-files`
    );
    return response.data;
  },

  /** Remove an attached reference file. */
  removeContextFile: async (messageId: number): Promise<{ success: boolean }> => {
    const response = await api.delete(
      `/api/chat/context-files/${messageId}`
    );
    return response.data;
  },
};

// User Profile API
export const userProfileAPI = {
  getProfile: async (): Promise<{
    id: number;
    email: string;
    full_name: string;
    is_active: boolean;
    role: string;
    user_type: string;
    email_verified: boolean;
    onboarding_completed: boolean;
    created_at: string;
    school_id?: number;
    subscription: {
      status: string;
      plan_type?: string;
      plan_tier?: string;
      current_period_start?: string;
      current_period_end?: string;
      next_billing_date?: string;
      cancel_at_period_end: boolean;
      cancel_at?: string | null;
      pending_plan_tier?: string | null;
      pending_billing_cycle?: string | null;
      pending_plan_effective_at?: string | null;
      amount?: number;
      currency: string;
    };
    usage: {
      daily_chats_used: number;
      daily_chats_limit: number;
      monthly_chats_used: number;
      monthly_chats_limit: number;
      monthly_transcripts_used: number;
      monthly_transcripts_limit: number;
      documents_uploaded: number;
      documents_limit: number;
      storage_used_mb: number;
      storage_limit_mb: number;
    };
  }> => {
    const response = await cachedGet('/api/user-profile/profile', {
      _cacheMs: 30_000,
      _staleOnRateLimitMs: 10 * 60_000,
    });
    return response.data;
  },

  updateProfile: async (data: {
    full_name?: string;
    email?: string;
    language?: 'en' | 'tr';
  }): Promise<{
    message: string;
    updated_fields: string[];
  }> => {
    const response = await api.put('/api/user-profile/profile', data);
    clearApiGetCache('/api/user-profile/profile');
    return response.data;
  },

  // Completes an email change from the link sent to the new address (SVC-1).
  // Needs the session that requested the change; revokes all sessions on success.
  confirmEmailChange: async (token: string): Promise<{ message: string; email?: string }> => {
    const response = await api.post('/api/user-profile/profile/email/confirm', { token });
    clearApiGetCache('/api/user-profile/profile');
    return response.data;
  },

  getUsageDetails: async (period: 'today' | 'this_week' | 'this_month' | 'last_30_days'): Promise<{
    period: string;
    start_date: string;
    end_date: string;
    requests_count: number;
    documents_uploaded: number;
    period_name: string;
  }> => {
    const response = await api.get(`/api/user-profile/usage/${period}`);
    return response.data;
  },

  deleteAccount: async (data: {
    password?: string;
    confirmation: string;
    reason?: string;
  }): Promise<{
    message: string;
    deletion_id: string;
  }> => {
    const response = await api.delete('/api/user-profile/account', { data });
    return response.data;
  },

  exportData: async (): Promise<{
    export_data: any;
    exported_at: string;
    export_format: string;
  }> => {
    const response = await api.post('/api/user-profile/export-data');
    return response.data;
  },
};

// Health check
export const healthAPI = {
  check: async (): Promise<{ message: string }> => {
    const response = await api.get('/health');
    return response.data;
  },
};

// Voice Transcription API
export const transcriptionAPI = {
  /**
   * Upload audio file and start transcription.
   * Uses multipart/form-data to match backend endpoint.
   */
  transcribe: async (
    file: File | { uri: string; type: string; name: string },
    options: TranscriptionOptions = {}
  ): Promise<TranscriptionUploadResponse> => {

    const formData = new FormData();

    if ('uri' in file) {
      // Handle Expo file (data URI, blob URI, or native file URI)
      if (file.uri.startsWith('data:') || file.uri.startsWith('blob:')) {
        // Expo web: data URI or blob URI - convert to blob
        let response: Response;
        try {
          response = await fetch(file.uri);
        } catch {
          throw new Error('UPLOAD_INTERRUPTED');
        }
        const blob = await response.blob();
        formData.append('file', blob, file.name || 'audio');
      } else {
        // React Native: pass the file via FormData using its URI
        formData.append(
          'file',
          {
            uri: file.uri,
            name: file.name || 'audio',
            type: file.type || 'audio/mpeg',
          } as any,
        );
      }
    } else {
      // Web File object
      formData.append('file', file);
    }

    // Append options as form fields
    if (options.language && options.language !== 'auto') {
      formData.append('language', options.language);
    }
    if (options.uiLanguage) {
      formData.append('ui_language', options.uiLanguage);
    }
    if (options.sessionId) {
      formData.append('session_id', options.sessionId.toString());
    }
    if (options.analysisInstructions) {
      formData.append('analysis_instructions', options.analysisInstructions);
    }
    if (options.includeInICFProgress !== undefined) {
      formData.append('include_in_icf_progress', options.includeInICFProgress.toString());
    }
    if (options.applyGrammarCorrection !== undefined) {
      formData.append('apply_grammar_correction', options.applyGrammarCorrection.toString());
    }
    if (options.enableDiarization !== undefined) {
      formData.append('enable_diarization', options.enableDiarization.toString());
    }

    // Use fetch instead of axios to avoid Content-Type conflicts with FormData
    const useCookies = USE_COOKIES;
    const token = useCookies ? null : await getAccessToken();
    let fetchResponse: Response;
    try {
      fetchResponse = await fetch(`${API_BASE_URL}/api/transcriptions/transcribe`, {
        method: 'POST',
        headers: {
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
          // ASYNC-6: one key per upload intent; a replay returns the same job.
          'Idempotency-Key': options.idempotencyKey || createIdempotencyKey(),
          // Don't set Content-Type - let browser handle multipart boundary
        },
        credentials: useCookies ? 'include' : 'same-origin',
        body: formData,
      });
    } catch {
      throw new Error('NETWORK_UNAVAILABLE');
    }

    if (!fetchResponse.ok) {
      let errorData: unknown = null;
      try {
        errorData = await fetchResponse.json();
      } catch { }
      throw transcribeErrorFromResponse(fetchResponse.status, errorData);
    }

    const data = await fetchResponse.json();
    clearApiGetCache('/api/chat/sessions');
    clearApiGetCache('/api/transcriptions/');
    clearApiGetCache('/api/transcriptions/usage');
    clearApiGetCache('/api/user-documents/');
    return data;
  },

  /**
   * List user's transcripts with pagination.
   */
  list: async (skip = 0, limit = 20): Promise<TranscriptionListResponse> => {
    const response = await cachedGet<TranscriptionListResponse>('/api/transcriptions/', {
      params: { skip, limit },
      _cacheMs: 15_000,
      _staleOnRateLimitMs: 5 * 60_000,
    });
    return response.data;
  },

  /**
   * Get transcription status for polling during processing.
   */
  getStatus: async (documentId: number): Promise<TranscriptionStatusResponse> => {
    const response = await api.get<TranscriptionStatusResponse>(
      `/api/transcriptions/${documentId}/status`
    );
    return response.data;
  },

  /**
   * Get full transcript content (decrypted).
   */
  getContent: async (documentId: number): Promise<TranscriptContent> => {
    const response = await api.get<TranscriptContent>(
      `/api/transcriptions/${documentId}/content`
    );
    return response.data;
  },

  /**
   * Save edited transcript segments (speaker labels, word corrections).
   * Returns updated transcript content.
   */
  saveSegments: async (documentId: number, segments: TranscriptSegment[]): Promise<TranscriptContent> => {
    const response = await api.patch<TranscriptContent>(
      `/api/transcriptions/${documentId}/segments`,
      { segments }
    );
    return response.data;
  },

  /**
   * Get presigned URL for audio playback.
   * Returns S3 presigned URL with short TTL for security.
   */
  getAudioUrl: async (documentId: number): Promise<TranscriptionAudioResponse> => {
    const response = await api.get<TranscriptionAudioResponse>(
      `/api/transcriptions/${documentId}/audio`
    );
    return response.data;
  },

  /**
   * Download transcript in specified format.
   * @param format - 'srt', 'vtt', or 'txt'
   * @param includeSpeakers - Whether to include speaker labels
   */
  download: async (
    documentId: number,
    format: 'srt' | 'vtt' | 'txt',
    includeSpeakers = true
  ): Promise<string> => {
    const response = await api.get<string>(
      `/api/transcriptions/${documentId}/download/${format}`,
      {
        params: { include_speakers: includeSpeakers },
        responseType: 'text',
      }
    );
    return response.data;
  },

  /**
   * Download transcript as a Word document formatted for ICF Performance Evaluation submission.
   */
  downloadIcfDocx: async (documentId: number): Promise<Blob> => {
    const response = await api.get(
      `/api/transcriptions/${documentId}/download/icf-docx`,
      {
        responseType: 'blob',
      }
    );
    return response.data;
  },

  /**
   * Download transcript as PDF with company logo.
   * Returns a Blob that can be downloaded or shared.
   */
  downloadPdf: async (documentId: number, uiLanguage?: string): Promise<Blob> => {
    const response = await api.get(
      `/api/transcriptions/${documentId}/download/pdf`,
      {
        params: uiLanguage ? { ui_language: uiLanguage } : undefined,
        responseType: 'blob',
      }
    );
    return response.data;
  },

  /**
   * Delete transcript and associated audio file.
   */
  delete: async (documentId: number): Promise<{ success: boolean; message: string }> => {
    const response = await api.delete(`/api/transcriptions/${documentId}`);
    clearApiGetCache('/api/transcriptions/');
    clearApiGetCache('/api/transcriptions/usage');
    clearApiGetCache('/api/user-documents/');
    return response.data;
  },

  /**
   * Get download URL for the original audio file.
   * Returns a presigned URL for S3 or streams directly for local storage.
   */
  downloadAudio: async (documentId: number): Promise<{ download_url?: string; filename: string }> => {
    const response = await api.get<{ download_url?: string; filename: string }>(
      `/api/transcriptions/${documentId}/audio/download`
    );
    return response.data;
  },

  /**
   * Get supported audio formats and limits.
   */
  getFormats: async (): Promise<TranscriptionFormatsResponse> => {
    const response = await cachedGet<TranscriptionFormatsResponse>('/api/transcriptions/formats', {
      _cacheMs: 5 * 60_000,
      _staleOnRateLimitMs: 30 * 60_000,
    });
    return response.data;
  },

  /**
   * Get current voice transcription minute entitlement and usage.
   */
  getUsage: async (): Promise<TranscriptionUsageResponse> => {
    const response = await cachedGet<TranscriptionUsageResponse>('/api/transcriptions/usage', {
      _cacheMs: 30_000,
      _staleOnRateLimitMs: 10 * 60_000,
    });
    return response.data;
  },

  /**
   * Poll for transcription completion with progress callback.
   * Similar to taskAPI.pollForCompletion but specific to transcription status.
   */
  pollForCompletion: async (
    documentId: number,
    onProgress?: (status: TranscriptionStatusResponse) => void,
    maxAttempts = 180, // Kept for backward compatibility
    intervalMs = 2000
  ): Promise<TranscriptContent> => {
    const inactivityTimeoutMs = Math.max(900_000, maxAttempts * intervalMs);
    let lastActivityAt = Date.now();

    while (true) {
      try {
        const status = await transcriptionAPI.getStatus(documentId);
        lastActivityAt = Date.now();

        if (onProgress) {
          onProgress(status);
        }

        if (status.status === 'completed') {
          return await transcriptionAPI.getContent(documentId);
        }

        if (status.status === 'failed') {
          const terminalError = new Error(status.error || 'Transcription failed');
          (terminalError as Error & { terminal: boolean }).terminal = true;
          throw terminalError;
        }
      } catch (error) {
        // A terminal backend failure must reach the UI immediately. Transport
        // errors are retried until the inactivity limit.
        if ((error as Error & { terminal?: boolean })?.terminal) {
          throw error;
        }
        if (Date.now() - lastActivityAt > inactivityTimeoutMs) {
          throw new Error('Transcription polling stalled - no status activity');
        }
      }

      await new Promise(resolve => setTimeout(resolve, intervalMs));
      if (Date.now() - lastActivityAt > inactivityTimeoutMs) {
        throw new Error('Transcription polling stalled - no status activity');
      }
    }
  },

  /**
   * Analyze an existing transcript using the RAG knowledge base.
   * Creates a new chat session linked to the source transcript.
   */
  analyzeTranscript: async (
    documentId: number,
    options: AnalysisOptions
  ): Promise<TranscriptAnalyzeResponse> => {
    const response = await aiAPI.sendMessage({
      documentId,
      analysisInstructions: options.analysisInstructions || options.customPrompt,
      includeInICFProgress: options.includeInIcfProgress,
      language: options.language,
      forceIntent: 'analyze',
      analysisMode: options.analysisMode,
      requestedLevel: options.requestedLevel,
      includeMarkerTable: options.includeMarkerTable,
      customPrompt: options.customPrompt,
      engagementId: options.engagementId,
      idempotencyKey: options.idempotencyKey,
    });
    return response as TranscriptAnalyzeResponse;
  },

  /**
   * List all analysis sessions linked to a transcript.
   */
  listAnalyses: async (documentId: number): Promise<TranscriptAnalysesResponse> => {
    const response = await api.get<TranscriptAnalysesResponse>(
      `/api/transcriptions/${documentId}/analyses`
    );
    return response.data;
  },
};

// =============================================================================
// User Documents API - Unified My Documents Hub
// =============================================================================

export interface UserDocumentItem {
  id: number;
  document_type: 'user_transcript' | 'user_analysis' | 'user_material';
  original_filename: string;
  file_size: number;
  content_type: string | null;
  created_at: string;
  // Voice transcript fields
  audio_duration_seconds?: number;
  transcript_language?: string;
  transcription_status?: string;
  grammar_correction_applied?: boolean;
  // Analysis document fields
  session_id?: number;
  processed?: boolean;
}

export interface UserDocumentListResponse {
  documents: UserDocumentItem[];
  total: number;
  skip: number;
  limit: number;
}

export interface DocumentDownloadResponse {
  download_url: string;
  filename: string;
  expires_in: number;
}

export const userDocumentsAPI = {
  /**
   * List all user's documents (voice transcripts + text analyses).
   */
  list: async (
    skip: number = 0,
    limit: number = 50,
    documentType?: 'user_transcript' | 'user_analysis'
  ): Promise<UserDocumentListResponse> => {
    const params = new URLSearchParams();
    params.append('skip', skip.toString());
    params.append('limit', limit.toString());
    if (documentType) {
      params.append('document_type', documentType);
    }
    const response = await cachedGet<UserDocumentListResponse>(
      `/api/user-documents/?${params.toString()}`,
      {
        _cacheMs: 30_000,
        _staleOnRateLimitMs: 10 * 60_000,
      }
    );
    return response.data;
  },

  /**
   * Get metadata for a specific document.
   */
  get: async (documentId: number): Promise<UserDocumentItem> => {
    const response = await api.get<UserDocumentItem>(
      `/api/user-documents/${documentId}`
    );
    return response.data;
  },

  /**
   * Get download URL for a document.
   * Rate limited to 5 downloads per minute.
   */
  download: async (documentId: number): Promise<DocumentDownloadResponse> => {
    const response = await api.get<DocumentDownloadResponse>(
      `/api/user-documents/${documentId}/download`
    );
    return response.data;
  },

  /**
   * Delete a document.
   * @param cascadeChat - If true, also delete the associated chat session
   */
  delete: async (
    documentId: number,
    cascadeChat: boolean = false
  ): Promise<{ success: boolean; message: string; chat_deleted: boolean }> => {
    const response = await api.delete(
      `/api/user-documents/${documentId}?cascade_chat=${cascadeChat}`
    );
    clearApiGetCache('/api/user-documents/');
    clearApiGetCache('/api/chat/sessions');
    clearApiGetCache('/api/transcriptions/');
    return response.data;
  },
};

// =============================================================================
// Document Sharing API
// =============================================================================

import type {
  ShareDocumentRequest,
  ShareRecipient,
  SharedDocument,
  SharedByMeDocument,
  DocumentRecipient,
  AddRecipientsRequest,
  MaterialUploadResponse,
  NotificationItem,
  MentorInfo,
  MentorInvitation,
} from '../types/sharing';

export const documentSharingAPI = {
  shareDocument: async (documentId: number, data: ShareDocumentRequest) => {
    const response = await api.post<{ message: string; shares_created: number }>(
      `/api/document-sharing/documents/${documentId}/share`,
      data
    );
    clearApiGetCache('/api/document-sharing/shared-with-me');
    clearApiGetCache('/api/document-sharing/shared-by-me');
    return response.data;
  },

  getSharedWithMe: async (skip = 0, limit = 50, documentType?: string) => {
    const params = new URLSearchParams({ skip: String(skip), limit: String(limit) });
    if (documentType) params.append('document_type', documentType);
    const response = await cachedGet<SharedDocument[]>(
      `/api/document-sharing/shared-with-me?${params}`,
      {
        _cacheMs: 30_000,
        _staleOnRateLimitMs: 10 * 60_000,
      }
    );
    return response.data;
  },

  getSharedByMe: async (skip = 0, limit = 50) => {
    const response = await cachedGet<SharedByMeDocument[]>(
      `/api/document-sharing/shared-by-me?skip=${skip}&limit=${limit}`,
      {
        _cacheMs: 30_000,
        _staleOnRateLimitMs: 10 * 60_000,
      }
    );
    return response.data;
  },

  getRecipients: async () => {
    const response = await api.get<ShareRecipient[]>('/api/document-sharing/recipients');
    return response.data;
  },

  revokeShare: async (shareId: number) => {
    const response = await api.delete<{ message: string }>(`/api/document-sharing/${shareId}`);
    clearApiGetCache('/api/document-sharing/shared-with-me');
    clearApiGetCache('/api/document-sharing/shared-by-me');
    return response.data;
  },

  uploadMaterial: async (formData: FormData) => {
    const response = await api.post<MaterialUploadResponse>(
      '/api/document-sharing/upload',
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    clearApiGetCache('/api/document-sharing/shared-with-me');
    clearApiGetCache('/api/document-sharing/shared-by-me');
    clearApiGetCache('/api/user-documents/');
    return response.data;
  },

  getDocumentRecipients: async (documentId: number) => {
    const response = await api.get<DocumentRecipient[]>(
      `/api/document-sharing/documents/${documentId}/recipients`
    );
    return response.data;
  },

  addDocumentRecipients: async (documentId: number, data: AddRecipientsRequest) => {
    const response = await api.post<{ message: string; shares_created: number }>(
      `/api/document-sharing/documents/${documentId}/recipients/add`,
      data
    );
    clearApiGetCache('/api/document-sharing/shared-with-me');
    clearApiGetCache('/api/document-sharing/shared-by-me');
    return response.data;
  },

  downloadMaterial: async (documentId: number) => {
    const response = await api.get(
      `/api/document-sharing/documents/${documentId}/download`,
      { responseType: 'blob' }
    );
    return response;
  },
};

// =============================================================================
// Notifications API
// =============================================================================

export const notificationsAPI = {
  list: async (unreadOnly = false, skip = 0, limit = 50) => {
    const params = new URLSearchParams({
      skip: String(skip),
      limit: String(limit),
      unread_only: String(unreadOnly),
    });
    const response = await api.get<NotificationItem[]>(`/api/notifications?${params}`);
    return response.data;
  },

  getUnreadCount: async () => {
    const response = await api.get<{ count: number }>('/api/notifications/unread-count');
    return response.data;
  },

  markRead: async (notificationId: number) => {
    const response = await api.post<{ message: string }>(
      `/api/notifications/${notificationId}/mark-read`
    );
    return response.data;
  },

  markAllRead: async () => {
    const response = await api.post<{ message: string }>('/api/notifications/mark-all-read');
    return response.data;
  },
};

// =============================================================================
// Student Management API (for school admins) — resend / revoke invites
// =============================================================================

export interface StudentInvitation {
  id: number;
  email: string;
  full_name: string;
  invitation_expires: string;
  is_accepted: boolean;
  created_at: string;
}

export const studentAPI = {
  listStudentInvitations: async (
    schoolId: number,
    status: 'pending' | 'all' = 'pending',
  ): Promise<StudentInvitation[]> => {
    const response = await api.get<StudentInvitation[]>(
      `/api/schools/${schoolId}/student-invitations?status=${status}`,
    );
    return response.data;
  },

  resendStudentInvitation: async (schoolId: number, invitationId: number) => {
    const response = await api.post<{ message: string; token_expires: string }>(
      `/api/schools/${schoolId}/student-invitations/${invitationId}/resend`,
    );
    return response.data;
  },

  cancelStudentInvitation: async (schoolId: number, invitationId: number) => {
    const response = await api.delete<{ message: string }>(
      `/api/schools/${schoolId}/student-invitations/${invitationId}`,
    );
    return response.data;
  },
};

// =============================================================================
// School Admin listing (active admins only; no invite list endpoint yet)
// =============================================================================

export interface SchoolAdminInfo {
  id: number;
  full_name: string;
  email: string;
  is_active: boolean;
  created_at: string;
}

export interface AdminInvitation {
  id: number;
  email: string;
  full_name: string | null;
  invited_by: number | null;
  invitation_expires: string | null;
  is_accepted: boolean;
  created_at: string;
}

export const schoolAdminAPI = {
  listAdmins: async (schoolId: number): Promise<SchoolAdminInfo[]> => {
    const response = await api.get<SchoolAdminInfo[]>(`/api/schools/${schoolId}/admins`);
    return response.data;
  },

  listAdminInvitations: async (
    schoolId: number,
    status: 'pending' | 'all' = 'pending',
  ): Promise<AdminInvitation[]> => {
    const response = await api.get<AdminInvitation[]>(
      `/api/schools/${schoolId}/admin-invitations?status=${status}`,
    );
    return response.data;
  },

  inviteAdmin: async (
    schoolId: number,
    email: string,
    fullName: string,
  ): Promise<AdminInvitation> => {
    const response = await api.post<AdminInvitation>(
      `/api/schools/${schoolId}/admin-invitations`,
      { email, full_name: fullName },
    );
    return response.data;
  },

  resendAdminInvitation: async (schoolId: number, invitationId: number) => {
    const response = await api.post<{ message: string; token_expires: string | null }>(
      `/api/schools/${schoolId}/admin-invitations/${invitationId}/resend`,
    );
    return response.data;
  },

  cancelAdminInvitation: async (schoolId: number, invitationId: number) => {
    const response = await api.delete<{ message: string }>(
      `/api/schools/${schoolId}/admin-invitations/${invitationId}`,
    );
    return response.data;
  },
};

// =============================================================================
// Mentor Management API (for school admins)
// =============================================================================

export const mentorAPI = {
  inviteMentor: async (schoolId: number, email: string, fullName: string) => {
    // `warning` (D20): set when the invitee already has an active personal subscription.
    const response = await api.post<{ message: string; invitation_id: number; warning?: string | null }>(
      `/api/schools/${schoolId}/mentors/invite`,
      { email, full_name: fullName, send_email: true }
    );
    return response.data;
  },

  listMentors: async (schoolId: number) => {
    const response = await api.get<MentorInfo[]>(`/api/schools/${schoolId}/mentors`);
    return response.data;
  },

  removeMentor: async (schoolId: number, mentorId: number) => {
    const response = await api.delete<{ message: string; shares_revoked: number }>(
      `/api/schools/${schoolId}/mentors/${mentorId}`
    );
    return response.data;
  },

  listMentorInvitations: async (schoolId: number) => {
    const response = await api.get<MentorInvitation[]>(
      `/api/schools/${schoolId}/mentor-invitations`
    );
    return response.data;
  },

  resendMentorInvitation: async (schoolId: number, invitationId: number) => {
    const response = await api.post<{ message: string; token_expires: string }>(
      `/api/schools/${schoolId}/mentor-invitations/${invitationId}/resend`
    );
    return response.data;
  },

  cancelMentorInvitation: async (schoolId: number, invitationId: number) => {
    const response = await api.delete<{ message: string }>(
      `/api/schools/${schoolId}/mentor-invitations/${invitationId}`
    );
    return response.data;
  },
};

// Trial API
export const trialAPI = {
  start: async (): Promise<{
    eligible: boolean;
    active: boolean;
    expired: boolean;
    used: boolean;
    session_id: number | null;
    document_id: number | null;
    messages_remaining: number;
    chat_count: number;
    hours_remaining: number;
    expires_at: string | null;
    kind?: 'legacy_no_card' | 'card_backed' | null;
    grandfathered?: boolean;
    analysis_limit: number;
    analysis_used: number;
    analysis_remaining: number;
    voice_minutes_limit?: number | null;
    voice_minutes_used?: number | null;
    voice_minutes_remaining?: number | null;
  }> => {
    const response = await api.post('/api/trial/start');
    clearApiGetCache('/api/trial/status');
    clearApiGetCache('/api/credits/status');
    return response.data;
  },

  getStatus: async (options?: ForceRefreshOptions): Promise<{
    eligible: boolean;
    active: boolean;
    expired: boolean;
    used: boolean;
    session_id: number | null;
    document_id: number | null;
    messages_remaining: number;
    chat_count: number;
    hours_remaining: number;
    expires_at: string | null;
    kind?: 'legacy_no_card' | 'card_backed' | null;
    grandfathered?: boolean;
    analysis_limit: number;
    analysis_used: number;
    analysis_remaining: number;
    voice_minutes_limit?: number | null;
    voice_minutes_used?: number | null;
    voice_minutes_remaining?: number | null;
  }> => {
    if (options?.force) {
      clearApiGetCache('/api/trial/status');
    }
    const response = await cachedGet('/api/trial/status', {
      _suppressUnauthorized: true,
      _skipAuthClear: true,
      _cacheMs: 30_000,
      _staleOnRateLimitMs: 10 * 60_000,
    } as any);
    return response.data;
  },

  startAnalysis: async (
    file:
      | File
      | {
          uri: string;
          name: string;
          type?: string | null;
          mimeType?: string | null;
          size?: number;
        },
    language: string
  ): Promise<any> => {
    const formData = new FormData();

    if ('uri' in file) {
      if (file.uri.startsWith('data:') || file.uri.startsWith('blob:')) {
        const fetchedBlob = await fetch(file.uri);
        const blob = await fetchedBlob.blob();
        formData.append('file', blob, file.name || 'document');
      } else {
        formData.append(
          'file',
          {
            uri: file.uri,
            name: file.name || 'document',
            type: file.type || file.mimeType || 'application/octet-stream',
          } as any,
        );
      }
    } else {
      formData.append('file', file);
    }

    formData.append('language', language);

    const useCookies = USE_COOKIES;
    const token = useCookies ? null : await getAccessToken();
    const fetchResponse = await fetch(`${API_BASE_URL}/api/trial/analyze`, {
      method: 'POST',
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        // Do not set Content-Type — let the runtime set the multipart boundary automatically
      },
      credentials: useCookies ? 'include' : 'same-origin',
      body: formData,
    });

    if (!fetchResponse.ok) {
      const errorData = await fetchResponse.json().catch(() => ({ detail: 'Trial analysis failed' }));
      throw apiErrorFromBody(errorData, fetchResponse.status, `HTTP ${fetchResponse.status}`);
    }

    return fetchResponse.json();
  },
};

// ───────────────────────────────────────────────────────────────────────
// Cohorts API (school-owned student groupings)
// ───────────────────────────────────────────────────────────────────────

export interface Cohort {
  id: number;
  school_id: number;
  name: string;
  description: string | null;
  lead_admin_user_id: number | null;
  lead_admin_name: string | null;
  started_at: string | null;
  ends_at: string | null;
  archived_at: string | null;
  /** ICF / EMCC / AC program level enum value (see /api/cohorts/vocabulary). */
  program_level: string | null;
  /** Feature keys explicitly enabled for this cohort (e.g. ["mentor_training"]). */
  enabled_features: string[];
  member_count: number;
  created_at: string;
}

export interface CohortMember {
  user_id: number;
  full_name: string | null;
  email: string;
  joined_at: string;
}

export interface CohortCsvInviteRowResult {
  row: number;
  email: string | null;
  full_name: string | null;
  status: 'invited' | 'added_existing' | 'already_member' | 'failed';
  reason: string | null;
  invitation_id: number | null;
  user_id: number | null;
  invitation_sent: boolean | null;
  /** D20: 'active_personal_subscription' when the invitee already pays for a personal plan. */
  warning?: string | null;
}

export interface CohortCsvInviteResponse {
  total_rows: number;
  invited_count: number;
  added_existing_count: number;
  skipped_count: number;
  failed_count: number;
  invitation_email_failed_count: number;
  seats_remaining: number;
  results: CohortCsvInviteRowResult[];
}

export type CohortCsvInviteFile =
  | File
  | {
      uri: string;
      name: string;
      type?: string | null;
      mimeType?: string | null;
    };

export const cohortsAPI = {
  list: async (schoolId: number, includeArchived = false): Promise<Cohort[]> => {
    const qs = includeArchived ? '?include_archived=true' : '';
    const response = await api.get<Cohort[]>(`/api/schools/${schoolId}/cohorts${qs}`);
    return response.data;
  },

  create: async (
    schoolId: number,
    payload: {
      name: string;
      description?: string | null;
      lead_admin_user_id?: number | null;
      started_at?: string | null;
      ends_at?: string | null;
    },
  ): Promise<Cohort> => {
    const response = await api.post<Cohort>(`/api/schools/${schoolId}/cohorts`, payload);
    return response.data;
  },

  get: async (schoolId: number, cohortId: number): Promise<Cohort> => {
    const response = await api.get<Cohort>(`/api/schools/${schoolId}/cohorts/${cohortId}`);
    return response.data;
  },

  update: async (
    schoolId: number,
    cohortId: number,
    payload: Partial<{
      name: string;
      description: string | null;
      lead_admin_user_id: number | null;
      started_at: string | null;
      ends_at: string | null;
      program_level: string | null;
      enabled_features: string[];
    }>,
  ): Promise<Cohort> => {
    const response = await api.patch<Cohort>(
      `/api/schools/${schoolId}/cohorts/${cohortId}`,
      payload,
    );
    return response.data;
  },

  archive: async (schoolId: number, cohortId: number): Promise<Cohort> => {
    const response = await api.post<Cohort>(
      `/api/schools/${schoolId}/cohorts/${cohortId}/archive`,
    );
    return response.data;
  },

  listMembers: async (schoolId: number, cohortId: number): Promise<CohortMember[]> => {
    const response = await api.get<CohortMember[]>(
      `/api/schools/${schoolId}/cohorts/${cohortId}/members`,
    );
    return response.data;
  },

  addMembers: async (
    schoolId: number,
    cohortId: number,
    userIds: number[],
  ): Promise<CohortMember[]> => {
    const response = await api.post<CohortMember[]>(
      `/api/schools/${schoolId}/cohorts/${cohortId}/members`,
      { user_ids: userIds },
    );
    return response.data;
  },

  inviteCsv: async (
    schoolId: number,
    cohortId: number,
    file: CohortCsvInviteFile,
    sendEmail = true,
  ): Promise<CohortCsvInviteResponse> => {
    const formData = new FormData();

    if ('uri' in file) {
      formData.append(
        'file',
        {
          uri: file.uri,
          name: file.name || 'cohort-invites.csv',
          type: file.type || file.mimeType || 'text/csv',
        } as any,
      );
    } else {
      formData.append('file', file);
    }
    formData.append('send_email', String(sendEmail));

    const token = USE_COOKIES ? null : await getAccessToken();
    const response = await fetch(
      `${API_BASE_URL}/api/schools/${schoolId}/cohorts/${cohortId}/invite-csv`,
      {
        method: 'POST',
        credentials: USE_COOKIES ? 'include' : 'same-origin',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: formData,
      },
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: 'CSV invite failed' }));
      const detail = typeof errorData.detail === 'string' ? errorData.detail : 'CSV invite failed';
      throw new Error(detail);
    }

    return response.json();
  },

  removeMember: async (
    schoolId: number,
    cohortId: number,
    userId: number,
  ): Promise<void> => {
    await api.delete(`/api/schools/${schoolId}/cohorts/${cohortId}/members/${userId}`);
  },

  transfer: async (
    schoolId: number,
    cohortId: number,
    userId: number,
  ): Promise<CohortMember> => {
    const response = await api.post<CohortMember>(
      `/api/schools/${schoolId}/cohorts/${cohortId}/transfer`,
      { user_id: userId },
    );
    return response.data;
  },

  getStudentCohort: async (
    schoolId: number,
    userId: number,
  ): Promise<Cohort | null> => {
    const response = await api.get<Cohort | null>(
      `/api/schools/${schoolId}/students/${userId}/cohort`,
    );
    return response.data;
  },
};

export default api;
